import { Text } from '@digibee/beehive-ui';
import { choose, iff, otherwise, when } from '@digibee/control-statements';
import { Level } from '@digibee/flow';
import { useSelector } from '@xstate/react';
import {
  GitCommit,
  NavArrowDown,
  NavArrowDownSolid,
  NavArrowRight,
  NavArrowRightSolid,
  Pin,
  Position,
  Refresh,
  WarningTriangle
} from 'iconoir-react';
import { useState } from 'react';
import { shallowEqual } from 'react-redux';

import { TreeNode } from '../Node';
import * as Elements from '../Tree.elements';
import useTreeContext from '../utils/useTreeContext';

type LevelNodeProps = {
  node: TreeNode<Level>;
  shouldInspectDebugLayout: boolean;
  disableFloatActions?: boolean;
};

const LevelNode = ({
  node,
  shouldInspectDebugLayout,
  disableFloatActions
}: LevelNodeProps) => {
  const [showingActiveButton, setShowingActiveButton] = useState(false);
  const actor = useTreeContext(state => state.actor);
  const goToLevel = useTreeContext(state => state.goToLevel);

  const isChildrenExpanded = useSelector(
    actor,
    state => state.context.expandedById[node.id],
    shallowEqual
  );

  const comesFromCurrentLevelComponent = useSelector(actor, state => {
    const { currentLevelComponent } = state.context;

    return currentLevelComponent
      ? currentLevelComponent.id() === node.item.parentComponent()?.id() ||
          node.item.comesFromComponent(currentLevelComponent.id())
      : false;
  });

  const emphasis =
    comesFromCurrentLevelComponent && !shouldInspectDebugLayout
      ? 'children'
      : undefined;

  const onClickNavigationAction = () => goToLevel(node);
  const onMouseEnter = () => setShowingActiveButton(true);
  const onMouseLeave = () => setShowingActiveButton(false);

  return (
    <Elements.Container
      data-testid='flow-tree-node-container'
      shouldInspectDebugLayout={shouldInspectDebugLayout}
      isLevel
    >
      <Elements.Item
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid='build-flow-tree-node-item'
        depthLeft={shouldInspectDebugLayout ? node.depth - 1 : node.depth}
        emphasis={emphasis}
        shouldInspectDebugLayout={shouldInspectDebugLayout}
      >
        {choose(
          when(node.id === 'root', () =>
            shouldInspectDebugLayout ? <GitCommit /> : <GitCommit />
          ),
          when(shouldInspectDebugLayout, () => (
            <>
              <Elements.ToggleWrapper
                onClick={() =>
                  actor.send({ type: 'TOGGLE_CHILDREN', id: node.id })
                }
              >
                {isChildrenExpanded ? <NavArrowDown /> : <NavArrowRight />}
              </Elements.ToggleWrapper>
              {(node.item as Level).type() === 'onProcess' ? (
                <Refresh />
              ) : (
                <>
                  <Refresh />
                  <WarningTriangle />
                </>
              )}
            </>
          )),
          otherwise(() => (
            <>
              <Elements.ToggleWrapper
                onClick={() =>
                  actor.send({ type: 'TOGGLE_CHILDREN', id: node.id })
                }
              >
                {isChildrenExpanded ? (
                  <NavArrowDownSolid />
                ) : (
                  <NavArrowRightSolid />
                )}
              </Elements.ToggleWrapper>
              {(node.item as Level).type() === 'onProcess' ? (
                <Refresh />
              ) : (
                <>
                  <Refresh />
                  <WarningTriangle />
                </>
              )}
            </>
          ))
        )}
        <Elements.Label
          type='level'
          shouldInspectDebugLayout={shouldInspectDebugLayout}
        >
          <Text
            variant='b3'
            disabled
            css={{
              fontStyle: shouldInspectDebugLayout ? 'italic' : 'normal',
              fontSize: shouldInspectDebugLayout ? '12px' : '14px'
            }}
          >
            {node.label}
          </Text>
        </Elements.Label>
        {iff(showingActiveButton && !disableFloatActions, () => (
          <Elements.FloatAction
            shouldInspectDebugLayout={shouldInspectDebugLayout}
          >
            {shouldInspectDebugLayout ? (
              <Position
                className='icon-smallest'
                onClick={onClickNavigationAction}
              />
            ) : (
              <Pin
                className='icon-smallest'
                onClick={onClickNavigationAction}
              />
            )}
          </Elements.FloatAction>
        ))}
      </Elements.Item>
    </Elements.Container>
  );
};

export default LevelNode;
